'use client';

import { useState } from 'react';
import { MatchDateTimeEditModal } from '@/components/tournament/fixture/match/MatchDateTimeEditModal';
import { toast } from '@/components/ui/use-toast';
import { updateMatchDateTime } from '@/api/tournament/tournamentApi';
import type { Availability, GridMatch } from '@/types/tournament.model';
import { teamLabel } from './grid-model';

interface GridEditMatchModalProps {
  /** `null` keeps the modal closed; a match opens it pre-filled with that match's data. */
  match: GridMatch | null;
  onOpenChange: (open: boolean) => void;
  tournamentScheduleAvailability: Availability[];
  /**
   * Refetches the grid so the card lands on its new cell after a successful save. Returns
   * whether the refetch itself succeeded — the PATCH can succeed while this fails (a timeout,
   * a 5xx right after saving), and `handleSave` below needs to tell those two apart instead of
   * showing an unconditional success toast over a screen that still has the match parked at its
   * old cell.
   */
  onSaved: () => Promise<boolean>;
}

/**
 * Wraps `MatchDateTimeEditModal` for the grid's "arbitrary time, one match" edit path.
 *
 * This goes through `updateMatchDateTime` (single match) instead of the grid's batch
 * `saveGridMoves`: the two write paths must never be live at the same time, or a save here
 * could leave an unsaved drag-and-drop draft (`TournamentGridPage`'s `moves` map) silently out
 * of sync with what the backend now has. `TournamentGridPage` enforces that by disabling the
 * pencil whenever a draft is pending (see `editDisabled` in `GridMatchCard`), so by the time
 * this component is asked to open, `moves` is guaranteed empty.
 *
 * `team1Object`/`team2Object` are deliberately not passed to `MatchDateTimeEditModal`: it wants
 * a `Team` there, whose `players: Player[]` needs `email`/`username`/`gender`/etc. — fields a
 * `GridPlayer` doesn't carry. Leaving them undefined just makes the modal fall back to its
 * initials avatars; the availability icons still work, since those come from
 * `team1Availability`/`team2Availability` instead.
 */
export function GridEditMatchModal({
  match,
  onOpenChange,
  tournamentScheduleAvailability,
  onSaved,
}: GridEditMatchModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!match) return null;

  const handleSave = async (startTime: string) => {
    setIsSubmitting(true);
    try {
      // The match's own tournament, not the one the grid page was opened from: a match here
      // can belong to a sibling tournament sharing the same club block, and that tournamentId
      // is what this endpoint's route expects.
      await updateMatchDateTime(match.tournamentId, match.matchId, startTime);
      const refreshed = await onSaved();
      // Closed either way: the PATCH itself already committed on the backend, so there's
      // nothing left for the modal to retry. Re-saving here would just hit the "the match
      // already plays at that time" rejection if the refetch is what's stale, not the write.
      onOpenChange(false);
      if (refreshed) {
        toast({ title: 'Horario actualizado' });
      } else {
        toast({
          title: 'Se guardó el horario, pero no se pudo actualizar la grilla',
          description: 'Recargá la página para ver el cambio reflejado.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'No se pudo actualizar el horario',
        description:
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Probá de nuevo en unos segundos.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MatchDateTimeEditModal
      match={match}
      team1Name={teamLabel(match.team1, 'Equipo 1')}
      team2Name={teamLabel(match.team2, 'Equipo 2')}
      team1Availability={match.team1Availability}
      team2Availability={match.team2Availability}
      tournamentScheduleAvailability={tournamentScheduleAvailability}
      open
      onOpenChange={onOpenChange}
      onSave={handleSave}
      isSubmitting={isSubmitting}
    />
  );
}
